/**
 * The Console API's routes this build calls, by area - one place, so a route
 * the server renames is changed once. Every write that is a catalogued action
 * takes the step-up (when its type needs one) as { challengeId, code } in the
 * body; the server decides which do (IP-76) - the three intake actions
 * (IP-96), the three fund-fee actions (IP-101) and, from IP-111, the nine
 * pricing and cash decisions of D-D (PRICE_OVERRIDE/APPROVE/REJECT,
 * MATCH_CLAIM/APPROVE/REJECT, LINE_CLEAR/RETURN, RECEIPT_REFUND). Since
 * migration 0016 PRICE_APPROVE publishes the price: there is no Trustee leg. The upload carries the file's facts, never its bytes: its answer
 * is the batch and a presigned form the browser posts the file on, straight
 * to the bucket (IP-91; containers/uploads/uploadFile.js postToStore).
 *
 * The live Unit Prices, Fund Register, Bank Statement and Settings > Calendar
 * screens (IP-112; design 2026-10-02 sections 6 and 8) read and act through
 * pricing, register, cash and settings below. A list's query is passed as it
 * is (queryString drops undefined, null and ''); its page cursor is the
 * server's nextCursor, sent back as ?cursor=.
 */

const enc = encodeURIComponent;

/** @param {ReturnType<import('./http.js').createHttp>} http */
export function createEndpoints(http) {
  return {
    // Sign-in: public routes, no token (auth/AuthGate.jsx).
    auth: {
      signIn: (email, password) => http.post('/auth/sign-in', { email, password }, { auth: false }),
      newPassword: (flow, newPassword) => http.post('/auth/new-password', { flow, newPassword }, { auth: false }),
      forgotPassword: (email) => http.post('/auth/forgot-password', { email }, { auth: false }),
      confirmForgotPassword: (email, code, newPassword) => http.post('/auth/forgot-password/confirm', { email, code, newPassword }, { auth: false }),
      mfa: (flow, code) => http.post('/auth/mfa', { flow, code }, { auth: false }),
      mfaSetupStart: (flow) => http.post('/mfa-setup/start', { flow }, { auth: false }),
      mfaSetupVerify: (flow, code) => http.post('/mfa-setup/verify', { flow, code }, { auth: false }),
      refresh: () => http.post('/auth/refresh', {}, { auth: false }),
      signOut: () => http.post('/auth/sign-out', {}, { auth: false }),
      activity: () => http.post('/auth/activity', {}),
    },

    me: () => http.get('/me'),
    profile: {
      changePassword: (currentPassword, newPassword) => http.post('/profile/password', { currentPassword, newPassword }),
    },

    stepUp: {
      start: (actionType, params) => http.post('/step-up/start', { actionType, params }),
    },

    // The bell (docs/notifications/implementation-guide.md 8.1): the signed-in person's own inbox,
    // keyed by the server on who is asking - never on anything sent here.
    notifications: {
      // ?limit, ?cursor (the last answer's nextCursor), ?unread=1 for unread only.
      list: (query = {}) => http.get('/notifications' + queryString(query)),
      unreadCount: () => http.get('/notifications/unread-count'),
      markRead: (ids) => http.post('/notifications/read', { ids }),
      markAllRead: () => http.post('/notifications/read-all', {}),
      // What the person receives, and what they muted.
      preferences: () => http.get('/notifications/preferences'),
      setMuted: (code, muted) => http.put('/notifications/preferences/' + enc(code), { muted }),
      // A one-time ticket that opens the socket at /ws (guide 7.2).
      socketTicket: () => http.post('/notifications/socket-ticket', {}),
    },

    access: {
      users: () => http.get('/access/users'),
      roles: () => http.get('/access/roles'),
      claims: () => http.get('/access/claims'),
      // The notification catalogue, its settings and subscribing roles (migration 0024) - the role dialog's checklist.
      notifications: () => http.get('/access/notifications'),
      // NOTIFICATION_CONFIG_SAVE: {enabled, forceEmail, emailCooldownMinutes} for one code.
      saveNotificationConfig: (code, body) => http.put('/access/notifications/' + enc(code), body),
      invite: (body) => http.post('/access/users', body),
      resendInvite: (uid) => http.post('/access/users/' + enc(uid) + '/resend', {}),
      setRoles: (uid, roles) => http.put('/access/users/' + enc(uid) + '/roles', { roles }),
      resetMfa: (uid, reason) => http.post('/access/users/' + enc(uid) + '/mfa-reset', { reason }),
      suspend: (uid, reason) => http.post('/access/users/' + enc(uid) + '/suspend', { reason }),
      reinstate: (uid, reason) => http.post('/access/users/' + enc(uid) + '/reinstate', { reason }),
      saveRole: (body) => http.post('/access/roles', body),
      retireRole: (role) => http.post('/access/roles/' + enc(role) + '/retire', {}),
    },

    intake: {
      accounts: () => http.get('/intake/accounts'),
      // ?fileClass, ?state, ?loadKind (INITIAL | DAILY, IP-103), ?limit.
      batches: (query = {}) => http.get('/intake/batches' + queryString(query)),
      // { withFindings, cursor, limit }: withFindings pages only the rows with findings (keyset on row_no).
      batch: (batchId, { withFindings, ...query } = {}) => http.get('/intake/batches/' + enc(batchId)
        + queryString({ ...query, withFindings: withFindings ? 'true' : undefined })),
      // A DAILY holdings batch against the register (IP-108): ?outcome (default: all but MATCHED), ?cursor, ?limit.
      holdingChecks: (batchId, query = {}) => http.get('/intake/batches/' + enc(batchId) + '/holding-checks' + queryString(query)),
      // The stored file as { batchId, fileName, contentBase64, sha256 } - only once the malware scan has cleared it.
      // Through the API like every other read (never from the bucket): the browser holds no S3 credential to read with.
      file: (batchId) => http.get('/intake/batches/' + enc(batchId) + '/file'),
      // {fileClass, businessDate, accountId?, fileName, sha256, contentLength, stepUp} -> {batch, upload: {url, fields, expiresAt}}
      upload: (body) => http.post('/intake/batches', body),
      // {files: [the facts above, 2 to 10], stepUp} -> {uploads: [{batch, upload}]}, in the files' order: one code for them all (INTAKE_UPLOAD_BATCH)
      uploadBatch: (body) => http.post('/intake/upload-batches', body),
      // {sha256, summarySha256, stepUp}
      accept: (batchId, body) => http.post('/intake/batches/' + enc(batchId) + '/accept', body),
      // {sha256, reason, stepUp}
      reject: (batchId, body) => http.post('/intake/batches/' + enc(batchId) + '/reject', body),
    },

    /*
     * The order queue and the Core's own approval (IP-82, IP-85, IP-86, IP-89).
     * review and sendBack are session-authorised (IP-76): no step-up code
     * travels with them. approve and reject need an authenticator code
     * (IP-199): the caller gets one through useStepUp (ORDER_APPROVE,
     * ORDER_REJECT) and passes it as stepUp. approve names each order's state_version
     * as the Console rendered it, so an order that moved since (G-1, APR-012) -
     * or was sent back since - is refused rather than approved at other figures.
     * review does the same (IP-127: an order that moved is skipped, APR-012,
     * "refresh and review again") and carries the reviewer's comment, one for
     * every order it names (3 to 1,000 characters); it sets no fee (IP-136).
     * reject takes an order in any open state (IP-130).
     */
    orders: {
      // states and orderTypes: lists, each sent as one comma-separated parameter. queue: REVIEW or APPROVE.
      list: ({ states, orderTypes, ...query } = {}) => http.get('/orders' + queryString({
        ...query,
        states: states?.length ? states.join(',') : undefined,
        orderTypes: orderTypes?.length ? orderTypes.join(',') : undefined
      })),
      detail: (orderId) => http.get('/orders/' + enc(orderId)),
      // feesWaived (IP-203), when given: orderId -> true to untick Apply fees on that leg, false to tick it again.
      review: (orderIds, expectedVersions, comment, feesWaived = null) => http.post('/orders/review', {
        orderIds, expectedVersions, comment, ...(feesWaived ? { feesWaived } : {})
      }),
      // stepUp: { challengeId, code } from useStepUp, bound to { orderIds, expectedVersions } (IP-199).
      approve: (orderIds, expectedVersions, stepUp) => http.post('/orders/approve', { orderIds, expectedVersions, stepUp }),
      sendBack: (orderIds, reason) => http.post('/orders/send-back', { orderIds, reason }),
      // stepUp: { challengeId, code } from useStepUp, bound to { orderIds, reason } (IP-199).
      reject: (orderIds, reason, stepUp) => http.post('/orders/reject', { orderIds, reason, stepUp }),
    },

    /*
     * Unit Prices (IP-61, IP-112). A run's four decisions are step-up (IP-111):
     * each body carries the figures or reason the code binds, plus
     * stepUp { challengeId, code }.
     */
    pricing: {
      // The IP-62 guard ORDER_APPROVE itself applies: per date, the active funds with no published price.
      guard: (dates) => http.get('/pricing/guard' + queryString({ date: dates.join(',') })),
      // ?fundId, ?from, ?to, ?includeSuperseded, ?limit, ?cursor.
      prices: (query = {}) => http.get('/pricing/prices' + queryString(query)),
      // The price history as one Excel file: the same filters, no ?limit or ?cursor. Answers {download: {fileName, url}, rowCount}.
      pricesExport: (query = {}) => http.get('/pricing/prices/export' + queryString(query)),
      // ?fundId, ?state, ?from, ?to, ?limit, ?cursor.
      runs: (query = {}) => http.get('/pricing/runs' + queryString(query)),
      // One run with its checks, batch, price and trail.
      run: (navRunId) => http.get('/pricing/runs/' + enc(navRunId)),
      // {checkCode, reason, stepUp}
      override: (navRunId, body) => http.post('/pricing/runs/' + enc(navRunId) + '/override', body),
      // {navPerUnit, sellingPrice, buyingPrice, buyingPriceWithExitFee, sellingPriceWithEntryFee, stepUp}
      approve: (navRunId, body) => http.post('/pricing/runs/' + enc(navRunId) + '/approve', body),
      // {reason, stepUp}
      reject: (navRunId, body) => http.post('/pricing/runs/' + enc(navRunId) + '/reject', body),
      // {navPerUnit, sellingPrice, buyingPrice, buyingPriceWithExitFee, stepUp}
    },

    /* The Fund Register (IP-112): reads only. */
    register: {
      funds: () => http.get('/register/funds'),
      // Keyset paged on holder_no: ?limit (<= 200), ?cursor.
      holders: (fundId, query = {}) => http.get('/register/funds/' + enc(fundId) + '/holders' + queryString(query)),
      // Every fund's holders, before a fund card is chosen: by account, holder, then fund.
      allocation: (query = {}) => http.get('/register/allocation' + queryString(query)),
      holder: (holderId) => http.get('/register/holders/' + enc(holderId)),
      // ?fundId, ?holderId, ?holderNo, ?from, ?to, ?txnType, ?source, ?limit, ?cursor.
      transactions: (query = {}) => http.get('/register/transactions' + queryString(query)),
      // Every row the same filters match as one Excel file (no ?limit or ?cursor). Answers {download: {fileName, url}, rowCount}.
      transactionsExport: (query = {}) => http.get('/register/transactions/export' + queryString(query)),
      // The INITIAL history (legacy_transaction, IP-109): ?fundId, ?holderId, ?from, ?to, ?txnType (S|R|SS|SR), ?cursor.
      history: (query = {}) => http.get('/register/history' + queryString(query)),
      // ?fundId, ?asAt, ?format (csv, the default: {fileName, csv, sha256, rowCount}; xlsx: {download: {fileName, url}, rowCount}).
      extract: (query = {}) => http.get('/register/extract' + queryString(query)),
    },

    /*
     * Bank Statement Transactions (IP-58, IP-67..71, IP-112). claim, approve,
     * reject, clear, returnLine, unreconcile, manualClear and refund are step-up (IP-111): the body plus
     * stepUp { challengeId, code }. addNote is a session action (LINE_NOTE):
     * a note is not a decision.
     */
    cash: {
      // ?fundId, ?reconciled (true | false), ?outstanding (true | false, IP-157), ?direction (CRDT | DBIT), ?limit (<= 100), ?cursor.
      lines: (query = {}) => http.get('/cash/lines' + queryString(query)),
      // LWM1-479 (IP-175): which statements are loaded, processing, failed or missing over the last 10 business days,
      // and whether reconciliation is complete - ?fundId (optional; every fund when none).
      statementStatus: ({ fundId } = {}) => http.get('/cash/statement-status' + queryString({ fundId })),
      // The fund's claimable subscriptions, each with its S1/S3 tier, what it is owed and whether this line is a part of it (IP-176).
      candidates: (lineId) => http.get('/cash/lines/' + enc(lineId) + '/candidates'),
      suggestions: (lineId) => http.get('/cash/lines/' + enc(lineId) + '/suggestions'),
      // {movementId, stepUp}
      claim: (lineId, body) => http.post('/cash/lines/' + enc(lineId) + '/claim', body),
      // {amount, stepUp}
      approve: (matchId, body) => http.post('/cash/matches/' + enc(matchId) + '/approve', body),
      // {reason, stepUp}
      reject: (matchId, body) => http.post('/cash/matches/' + enc(matchId) + '/reject', body),
      // {clearedOn, bankReference, reason, stepUp}
      clear: (lineId, body) => http.post('/cash/lines/' + enc(lineId) + '/clear', body),
      // {returnedOn, reason, stepUp}
      returnLine: (lineId, body) => http.post('/cash/lines/' + enc(lineId) + '/return', body),
      // {reason, stepUp}: an automatic match undone, the reason its comment (IP-138, LINE_UNRECONCILE)
      unreconcile: (lineId, body) => http.post('/cash/lines/' + enc(lineId) + '/unreconcile', body),
      // {reason, stepUp}: a left-over line cleared by hand (IP-157, LINE_MANUAL_CLEAR)
      manualClear: (lineId, body) => http.post('/cash/lines/' + enc(lineId) + '/manual-clear', body),
      // {refundReference, reason, stepUp}
      refund: (lineId, body) => http.post('/cash/lines/' + enc(lineId) + '/refund', body),
      // Newest first, a page at a time (cash/notes.js lineNotes: 50 by default): ?limit (<= 200), ?cursor - the older page.
      notes: (lineId, query = {}) => http.get('/cash/lines/' + enc(lineId) + '/notes' + queryString(query)),
      // {lineId, note}: LINE_NOTE's params (design 6), the line also in the path.
      addNote: (lineId, body) => http.post('/cash/lines/' + enc(lineId) + '/notes', body),
      match: (matchId) => http.get('/cash/matches/' + enc(matchId)),
      // GET /payouts/redemption-notes (core.payout.manage): {download?: {fileName, url}, funds: [{fundId, count} | {fundId, error}]}.
      redemptionNotes: (formats = ['docx']) => http.get('/payouts/redemption-notes' + queryString({ formats: formats.join(',') })),
    },

    /*
     * Settings > Fund config (IP-101). A fee change is proposed by one person and
     * approved or rejected by another; each of the three needs a fresh
     * authenticator code, so the body carries stepUp { challengeId, code }
     * (stepup/useStepUp.js).
     */
    settings: {
      // Settings > Calendar (IP-112): ?from, ?to (at most 366 days; default this month in Colombo), ?calendarCode.
      calendar: (query = {}) => http.get('/settings/calendar' + queryString(query)),
      fundConfig: () => http.get('/settings/fund-config'),
      propose: (body) => http.post('/settings/fund-config/proposals', body),
      approve: (proposalId, body) => http.post('/settings/fund-config/proposals/' + enc(proposalId) + '/approve', body),
      reject: (proposalId, body) => http.post('/settings/fund-config/proposals/' + enc(proposalId) + '/reject', body),
    },

    /*
     * The business day (IP-134, IP-135; design 2026-10-06 sections 5.5, 5.6
     * and 8.1). days is the one read BusinessDayProvider polls: the live days,
     * the latest closed one, the open dealing day and the review pause - or
     * only ?date. advance is DAY_ADVANCE, session-authorised (core.book.close,
     * no step-up code): the day in the path, { force, reason } in the body, a
     * force needing its reason.
     */
    books: {
      days: ({ date } = {}) => http.get('/books/days' + queryString({ date })),
      advance: (businessDate, { force = false, reason } = {}) => http.post('/books/days/' + enc(businessDate) + '/advance', { force, reason }),
      // IP-195: DAY_END_RUN - starts the Day end process for the day now, with an authenticator code.
      dayEnd: (businessDate, { reason, stepUp }) => http.post('/books/days/' + enc(businessDate) + '/day-end', { reason, stepUp }),
      // The day-end analytics feeds (design 9): the dates, one date, a file, and the two step-up actions.
      feeds: ({ attention = true, limit } = {}) => http.get('/books/feeds' + queryString({ attention: attention ? 1 : 0, limit })),
      feedDay: (businessDate) => http.get('/books/days/' + enc(businessDate) + '/feeds'),
      feedFile: (businessDate, feed, version) => http.get('/books/days/' + enc(businessDate) + '/feeds/' + enc(feed) + '/file' + queryString({ version })),
      feedsResend: (businessDate, { reason, stepUp }) => http.post('/books/days/' + enc(businessDate) + '/feeds/resend', { reason, stepUp }),
      feedsWmWaive: (businessDate, { reason, stepUp }) => http.post('/books/days/' + enc(businessDate) + '/feeds/wm-waiver', { reason, stepUp }),
    },
  };
}

function queryString(query) {
  const pairs = Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== '');
  return pairs.length ? '?' + pairs.map(([key, value]) => enc(key) + '=' + enc(String(value))).join('&') : '';
}
